// components/ConsentBanner.js
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from 'context/ConsentContext';
import styles from './styles.module.scss';

const ConsentBanner = () => {
  const { t } = useTranslation('global');
  const bannerRef = useRef(null);
  const { 
    consent, 
    updateConsent, 
    acceptAll, 
    rejectAll,
    acceptSelected,
    isBannerVisible,
    setIsBannerVisible,
    showPreferences,
    setShowPreferences
  } = useConsent();

  // Log when banner closes (only in development)
  useEffect(() => {
    if (!isBannerVisible && process.env.NODE_ENV === 'development') {
      console.log('[ConsentBanner] Banner closed at:', new Date().toISOString());
    }
  }, [isBannerVisible]);

  // Intercept global click handlers that might close the banner
  useEffect(() => {
    if (!isBannerVisible) return;

    const handleGlobalClick = (e) => {
      if (bannerRef.current && bannerRef.current.contains(e.target)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[ConsentBanner] Click inside banner - allowing normal handling');
        }
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[ConsentBanner] Click outside banner - ignoring');
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [isBannerVisible]);

  if (!isBannerVisible) return null;

  const handlePreferenceChange = (e, type) => {
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[ConsentBanner] Checkbox clicked for:', type, 'Current value:', !consent[type]);
    }
    updateConsent({ [type]: !consent[type] });
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[ConsentBanner] Checkbox clicked - propagation stopped');
    }
  };

  const handleLabelClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[ConsentBanner] Label clicked - propagation stopped');
    }
  };

  const handleCustomizeClick = (e) => {
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[ConsentBanner] Customize button clicked - opening preferences');
    }
    setShowPreferences(true);
  };

  return (
    <div 
      ref={bannerRef}
      className={styles.consentBanner}
      role="dialog"
      aria-labelledby="consent-title"
      aria-modal="true"
    >
      <div className={styles.bannerContent}>
        <div className={styles.bannerHeader}>
          <h3 id="consent-title">{t('cookies.title', 'Cookie Preferences')}</h3>
        </div>
        
        <p className={styles.bannerText}>
          {t('cookies.description', 'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. You can customize your preferences below.')}
        </p>

        {!showPreferences ? (
          <div className={styles.buttonGroup}>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                acceptAll();
              }} 
              className={styles.acceptAllBtn}
            >
              {t('cookies.acceptAll', 'Accept All')}
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                rejectAll();
              }} 
              className={styles.rejectAllBtn}
            >
              {t('cookies.rejectAll', 'Reject All')}
            </button>
            <button 
              onClick={handleCustomizeClick}
              className={styles.customizeBtn}
            >
              {t('cookies.customize', 'Customize')}
            </button>
          </div>
        ) : (
          <div className={styles.preferencesPanel}>
            <h4>{t('cookies.customizePreferences', 'Customize Consent Preferences')}</h4>
            
            <div className={styles.preferenceItem}>
              <div className={styles.preferenceHeader}>
                <label className={styles.checkboxLabel} onClick={handleLabelClick}>
                  <input 
                    type="checkbox" 
                    checked={consent.necessary}
                    disabled={true}
                    className={styles.checkbox}
                    onClick={handleCheckboxClick}
                    onChange={() => {}}
                  />
                  <strong>{t('cookies.necessary', 'Necessary')} <span className={styles.alwaysActive}>({t('cookies.alwaysActive', 'Always Active')})</span></strong>
                </label>
              </div>
              <p className={styles.preferenceDescription}>
                {t('cookies.necessaryDesc', 'These cookies are essential for the website to function and cannot be disabled. They enable basic functions like page navigation and access to secure areas.')}
              </p>
            </div>

            <div className={styles.preferenceItem}>
              <div className={styles.preferenceHeader}>
                <label className={styles.checkboxLabel} onClick={handleLabelClick}>
                  <input 
                    type="checkbox" 
                    checked={consent.analytics}
                    onChange={(e) => handlePreferenceChange(e, 'analytics')}
                    className={styles.checkbox}
                    onClick={handleCheckboxClick}
                  />
                  <strong>{t('cookies.analytics', 'Analytics Cookies')}</strong>
                </label>
              </div>
              <p className={styles.preferenceDescription}>
                {t('cookies.analyticsDesc', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services.')}
              </p>
            </div>

            <div className={styles.preferenceItem}>
              <div className={styles.preferenceHeader}>
                <label className={styles.checkboxLabel} onClick={handleLabelClick}>
                  <input 
                    type="checkbox" 
                    checked={consent.marketing}
                    onChange={(e) => handlePreferenceChange(e, 'marketing')}
                    className={styles.checkbox}
                    onClick={handleCheckboxClick}
                  />
                  <strong>{t('cookies.marketing', 'Marketing Cookies')}</strong>
                </label>
              </div>
              <p className={styles.preferenceDescription}>
                {t('cookies.marketingDesc', 'These cookies track your browsing habits across websites to deliver targeted advertisements that are relevant to you. They may be set by our advertising partners.')}
              </p>
            </div>

            <div className={styles.preferenceItem}>
              <div className={styles.preferenceHeader}>
                <label className={styles.checkboxLabel} onClick={handleLabelClick}>
                  <input 
                    type="checkbox" 
                    checked={consent.preferences}
                    onChange={(e) => handlePreferenceChange(e, 'preferences')}
                    className={styles.checkbox}
                    onClick={handleCheckboxClick}
                  />
                  <strong>{t('cookies.preferences', 'Functional Cookies')}</strong>
                </label>
              </div>
              <p className={styles.preferenceDescription}>
                {t('cookies.preferencesDesc', 'These cookies enable personalized features and functionality, such as remembering your preferences and language settings for a personalized experience.')}
              </p>
            </div>

            <div className={styles.preferenceButtons}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  acceptSelected();
                }} 
                className={styles.saveBtn}
              >
                {t('cookies.savePreferences', 'Save Preferences')}
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  acceptAll();
                }} 
                className={styles.acceptAllBtn}
              >
                {t('cookies.acceptAll', 'Accept All')}
              </button>
            </div>
          </div>
        )}

        <div className={styles.bannerFooter}>
          <a 
            href="/static/politique-confidentialite" 
            className={styles.footerLink}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer.privacyPolicy', 'Privacy Policy')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;